import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SING_COLORS } from '@/constants/marketing-display.constants';
import { AlertCircle, CheckCircle, Info, Mail, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationsApi } from '../../api/applicationsApi';
import { SECTION_CONFIGS } from '../../constants/sectionConfig.constants';
import type { Application } from '../../types/application.types';
import { CollapsibleSection } from '../shared/CollapsibleSection';

interface ValidationStepProps {
  application: Application;
  allStepsVisited: boolean;
}

const ACCEPT_COLOR = SING_COLORS.BLUE; // #00758d
const REJECT_COLOR = SING_COLORS.BURGUNDY; // #4A041E

export const ValidationStep: React.FC<ValidationStepProps> = ({ application, allStepsVisited }) => {
  const navigate = useNavigate();
  const config = SECTION_CONFIGS.validation;
  const [action, setAction] = useState<'accept' | 'reject' | null>(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = () => setAction('accept');
  const handleReject = () => setAction('reject');
  const handleCancel = () => {
    setAction(null);
    setMessage('');
  };

  const handleSubmit = async () => {
    if (!action) return;
    if (action === 'reject' && !message.trim()) return;
    setIsSubmitting(true);

    try {
      if (action === 'accept') {
        await applicationsApi.approve(application.id, message);
      } else {
        await applicationsApi.reject(application.id, message);
      }

      navigate(`/applications/${application.id}/success?action=${action}`);
    } catch (error) {
      console.error('[ValidationStep] Error submitting decision:', error);
      setIsSubmitting(false);
    }
  };

  if (!allStepsVisited) {
    return (
      <CollapsibleSection title={config.title} color={config.color} icon={config.icon} defaultOpen={config.defaultOpen}>
        <div
          className="border-l-4 rounded-lg p-4 flex items-start gap-3"
          style={{
            backgroundColor: `${SING_COLORS.GOLD}15`,
            borderColor: SING_COLORS.GOLD,
          }}
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: SING_COLORS.GOLD }} />
          <p className="text-sm text-slate-800">
            Vous devez consulter toutes les étapes avant de pouvoir valider ou refuser ce dossier.
          </p>
        </div>
      </CollapsibleSection>
    );
  }

  if (action === null) {
    return (
      <CollapsibleSection title={config.title} color={config.color} icon={config.icon} defaultOpen={config.defaultOpen}>
        <div className="space-y-6">
          {/* Texte explicatif */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-4">
              <Info className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-base font-semibold text-slate-800 mb-2">Décision finale sur le dossier</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Vous avez examiné toutes les informations du dossier. Votre décision déclenchera automatiquement
                  l'envoi d'un email au client pour l'informer du statut de sa demande.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-white border rounded-lg p-4" style={{ borderColor: `${ACCEPT_COLOR}40` }}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4" style={{ color: ACCEPT_COLOR }} />
                  <span className="text-sm font-semibold" style={{ color: ACCEPT_COLOR }}>
                    Acceptation
                  </span>
                </div>
                <p className="text-xs text-slate-600">
                  Le client recevra une confirmation par email et pourra procéder aux étapes suivantes.
                </p>
              </div>

              <div className="bg-white border rounded-lg p-4" style={{ borderColor: `${REJECT_COLOR}40` }}>
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4" style={{ color: REJECT_COLOR }} />
                  <span className="text-sm font-semibold" style={{ color: REJECT_COLOR }}>
                    Refus
                  </span>
                </div>
                <p className="text-xs text-slate-600">
                  Le client sera notifié avec la raison du refus et pourra soumettre un nouveau dossier.
                </p>
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="text-center py-4">
            <h3 className="text-lg font-semibold text-slate-800 mb-6">Que souhaitez-vous faire avec ce dossier ?</h3>

            <div className="flex gap-4 justify-center">
              <button
                onClick={handleAccept}
                className="inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-xl transition-all shadow-sm hover:shadow-md"
                style={{
                  backgroundColor: ACCEPT_COLOR,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = SING_COLORS.DARK;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = ACCEPT_COLOR;
                }}
              >
                <CheckCircle className="w-5 h-5" />
                <span>Accepter le dossier</span>
              </button>

              <button
                onClick={handleReject}
                className="inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-xl transition-all shadow-sm hover:shadow-md"
                style={{
                  backgroundColor: REJECT_COLOR,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = SING_COLORS.MAGENTA;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = REJECT_COLOR;
                }}
              >
                <XCircle className="w-5 h-5" />
                <span>Refuser le dossier</span>
              </button>
            </div>
          </div>
        </div>
      </CollapsibleSection>
    );
  }

  const currentColor = action === 'accept' ? ACCEPT_COLOR : REJECT_COLOR;

  return (
    <CollapsibleSection
      title={action === 'accept' ? 'Accepter le dossier' : 'Refuser le dossier'}
      color={currentColor}
      icon={action === 'accept' ? CheckCircle : XCircle}
      defaultOpen={true}
    >
      <div className="space-y-6">
        {/* Alerte notification email */}
        <div
          className="border-l-4 rounded-lg p-4 flex items-start gap-3"
          style={{
            backgroundColor: `${currentColor}15`,
            borderColor: currentColor,
          }}
        >
          <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: currentColor }} />
          <div className="flex-1">
            <h4 className="text-sm font-semibold mb-1" style={{ color: currentColor }}>
              {action === 'accept' ? 'Email de confirmation' : 'Email de refus'}
            </h4>
            <p className="text-sm text-slate-700">
              {action === 'accept'
                ? 'Un email de confirmation sera automatiquement envoyé au client avec les prochaines étapes à suivre.'
                : 'Un email sera envoyé au client avec la raison du refus. Le dossier sera archivé et le client pourra soumettre une nouvelle demande.'}
            </p>
          </div>
        </div>

        {/* Champ message */}
        <div className="space-y-2">
          <Label htmlFor="message" className="text-sm font-semibold text-slate-700">
            {action === 'accept' ? 'Message de confirmation (optionnel)' : 'Raison du refus (obligatoire)'}
          </Label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              action === 'accept'
                ? 'Ajoutez un message personnalisé pour le client (ex: prochaines étapes, coordonnées de contact)...'
                : 'Expliquez clairement la raison du refus (ex: documents manquants, critères non remplis)...'
            }
            rows={5}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm resize-none"
          />
          {action === 'reject' && message.trim() === '' && (
            <p className="text-sm flex items-center gap-1" style={{ color: REJECT_COLOR }}>
              <AlertCircle className="w-4 h-4" />
              <span>La raison du refus est obligatoire</span>
            </p>
          )}
          <p className="text-xs text-slate-500">
            {action === 'accept'
              ? "Ce message sera inclus dans l'email de confirmation envoyé au client."
              : 'Soyez précis et professionnel. Ce message aidera le client à comprendre les raisons du refus.'}
          </p>
        </div>

        {/* Boutons d'action */}
        <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting} className="px-6">
            Annuler
          </Button>

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (action === 'reject' && !message.trim())}
            className="px-6 py-2 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: currentColor,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting && !(action === 'reject' && !message.trim())) {
                e.currentTarget.style.backgroundColor = action === 'accept' ? SING_COLORS.DARK : SING_COLORS.MAGENTA;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = currentColor;
            }}
          >
            {isSubmitting
              ? 'Envoi en cours...'
              : action === 'accept'
                ? "Confirmer l'acceptation"
                : 'Confirmer le refus'}
          </button>
        </div>
      </div>
    </CollapsibleSection>
  );
};
